import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/src/store'
import { searchUser } from '@/src/store/apps/gygUser'
import {
  getIframeParams,
  clearIframeParams,
  storeIframeParams,
  getStoredIframeParams,
  clearStoredIframeParamsExceptTicket,
  isIframeContext,
  IframeParams
} from '@/src/utils/iframeUtils'
import { useAuth } from '@/src/hooks/useAuth'

export const useIframeFlow = () => {
  const router = useRouter()
  const dispatch = useDispatch<AppDispatch>()
  const auth = useAuth()
  const [iframeParams, setIframeParams] = useState<IframeParams | null>(null)
  const [iframeProcessed, setIframeProcessed] = useState(false)
  const [searchTriggered, setSearchTriggered] = useState(false)
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)

  const { gygUsers, pending, errors } = useSelector((state: RootState) => state.gygUser)

  // Don't process iframe flow on login page or when in iframe context on login
  const isLoginPage = router.pathname === '/login'
  const isIframe = isIframeContext()

  // Get iframe params on mount (from URL or localStorage)
  useEffect(() => {
    // First check URL parameters
    let params = getIframeParams()

    // If no URL params, check stored params (for after login)
    if (!params) {
      params = getStoredIframeParams()
    }

    if (params) {
      setIframeParams(params)
    }
  }, [])

  // Check for stored iframe params after login
  useEffect(() => {
    if (auth.user && !iframeParams && !iframeProcessed) {
      // User is logged in but no iframe params, check stored params
      const storedParams = getStoredIframeParams()
      if (storedParams) {
        setIframeParams(storedParams)
      }
    }
  }, [auth.user, iframeParams, iframeProcessed])

  // Handle iframe flow when user is not logged in
  useEffect(() => {
    // Store iframe params if user is not logged in and we have params
    if (iframeParams && !auth.user && !iframeProcessed) {
      // Always store iframe params before any redirect
      storeIframeParams(iframeParams)

      // Only redirect to login if not already on login page and not in iframe context
      if (!isLoginPage && !isIframe) {
        const returnUrl = encodeURIComponent(window.location.href)
        router.push(`/login?returnUrl=${returnUrl}`)
      }
    }
  }, [iframeParams, auth.user, iframeProcessed, router, isLoginPage, isIframe])

  // Handle iframe flow after login - run when user is logged in and we have iframe params
  useEffect(() => {
    if (iframeParams && auth.user && !iframeProcessed && !searchTriggered) {
      // Trigger search for the iframe email
      dispatch(searchUser(iframeParams.email))
      setSearchTriggered(true)

      // Set a timeout to handle backend server being down (30 seconds)
      const timeout = setTimeout(() => {
        if (pending) {
          // If still pending after 30 seconds, assume backend is down
          clearIframeParams()
          clearStoredIframeParamsExceptTicket() // Keep ticket number for refund
          setIframeProcessed(true)

          // Use push instead of replace to avoid abort errors
          // Add small delay to prevent rapid navigation changes
          setTimeout(() => {
            router.push('/app/customer-portal/users/')
          }, 100)
        }
      }, 30000)

      setSearchTimeout(timeout)
    }
  }, [iframeParams, auth.user, iframeProcessed, searchTriggered, dispatch, pending, router])

  // Handle search results - process after search completes
  useEffect(() => {
    if (iframeParams && auth.user && !iframeProcessed && searchTriggered && !pending && gygUsers.length > 0) {
      // Clear timeout if search completed successfully
      if (searchTimeout) {
        clearTimeout(searchTimeout)
        setSearchTimeout(null)
      }

      // Search completed, process results
      if (gygUsers.length === 1) {
        // Exactly one user found, navigate to user detail page
        const userId = gygUsers[0].id
        clearIframeParams()
        clearStoredIframeParamsExceptTicket() // Keep ticket number for refund
        setIframeProcessed(true)

        // Use push instead of replace to avoid abort errors
        // Add small delay to prevent rapid navigation changes
        setTimeout(() => {
          router.push(`/app/customer-portal/users/${userId}`)
        }, 100)
      } else {
        // Multiple users found, navigate to user list
        clearIframeParams()
        clearStoredIframeParamsExceptTicket() // Keep ticket number for refund
        setIframeProcessed(true)

        // Use push instead of replace to avoid abort errors
        // Add small delay to prevent rapid navigation changes
        setTimeout(() => {
          router.push('/app/customer-portal/users/')
        }, 100)
      }
    }
  }, [iframeParams, auth.user, iframeProcessed, searchTriggered, pending, gygUsers, router, searchTimeout])

  // Handle case when search returns no results or fails
  useEffect(() => {
    if (iframeParams && auth.user && !iframeProcessed && searchTriggered && !pending) {
      // Clear timeout if search completed
      if (searchTimeout) {
        clearTimeout(searchTimeout)
        setSearchTimeout(null)
      }

      if (gygUsers.length === 0) {
        // Search completed with no results, navigate to user list
        clearIframeParams()
        clearStoredIframeParamsExceptTicket() // Keep ticket number for refund
        setIframeProcessed(true)

        // Use push instead of replace to avoid abort errors
        // Add small delay to prevent rapid navigation changes
        setTimeout(() => {
          router.push('/app/customer-portal/users/')
        }, 100)
      }
    }
  }, [iframeParams, auth.user, iframeProcessed, searchTriggered, pending, gygUsers, router, searchTimeout])

  // Handle search errors - this should take priority over successful results
  useEffect(() => {
    if (iframeParams && auth.user && !iframeProcessed && searchTriggered && errors && Object.keys(errors).length > 0) {
      // Clear timeout if error occurred
      if (searchTimeout) {
        clearTimeout(searchTimeout)
        setSearchTimeout(null)
      }

      // Search failed (backend error, network error, etc.), navigate to user list
      // Don't use stale data from previous searches
      clearIframeParams()
      clearStoredIframeParamsExceptTicket() // Keep ticket number for refund
      setIframeProcessed(true)

      // Use push instead of replace to avoid abort errors
      // Add small delay to prevent rapid navigation changes
      setTimeout(() => {
        router.push('/app/customer-portal/users/')
      }, 100)
    }
  }, [iframeParams, auth.user, iframeProcessed, searchTriggered, errors, router, searchTimeout])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
    }
  }, [searchTimeout])

  return {
    iframeParams,
    isProcessingIframe: pending,
    handleIframeFlow: () => {
      // This function is kept for compatibility but the logic is now handled by useEffect hooks
    }
  }
}
